import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { EllipsisHorizontalIcon, PlusIcon } from '@heroicons/react/24/outline';
import PlaylistPicker from '../playlists/PlaylistPicker';
import GoogleAuthButton from '../auth/GoogleAuthButton';
import Modal from '../ui/Modal';
import useUserStore from '../../stores/userStore';
import useQueueStore from '../../stores/queueStore';
import useEventStore from '../../stores/eventStore';
import { useRoleStore } from '../../stores/roleStore';
import { fetchImportedPlaylists } from '../../services/importedPlaylists';
import {
  UserLibraryReadError,
  readUserPlaylistItemsPage,
} from '../../services/userLibrary';
import {
  syncImportedPlaylist,
  SYNC_OUTCOMES,
  hasSufficientPlaylistCoverage,
} from '../../services/playlistSync';
import { describePlaylistLoadFailure } from '../../services/youtubePlaylists';
import { refreshDueProviderMirrors } from '../../services/providerRefresh';
import { hasYouTubeAuthorization } from '../../services/youtubeAuth';
import { findParticipant } from '../../utils/identity';
import { LINK_OUTCOME } from '../../utils/authIdentity';
import { resolveInPartyGoogleAuth, IN_PARTY_GOOGLE_ACTION } from '../../utils/inPartyGoogleAuth';
import { cleanupStores } from '../../utils/storeUtils';
import { describeError } from '../../utils/errorLog';
import { removePlaylistFromMyMusic } from '../../services/playlistRemoval';
import FavoritesSection from './FavoritesSection';

function emptyPlaylistState() {
  return {
    tracks: [],
    nextCursor: null,
    hasMore: false,
    source: null,
    error: null,
    empty: false,
  };
}

function coverageLooksIncomplete(page, playlist) {
  const known = Number.isSafeInteger(page?.knownItemCount)
    ? page.knownItemCount
    : (Number.isSafeInteger(playlist?.itemCount) ? playlist.itemCount : 0);
  if (!(known > 0)) return false;
  if (Number.isSafeInteger(page?.loadedItemCount)) {
    return !hasSufficientPlaylistCoverage({
      loadedItemCount: page.loadedItemCount,
      knownItemCount: known,
    });
  }
  return !page.hasMore && page.fetchedCount < known;
}

function firestoreEntry(page) {
  return {
    tracks: page.tracks,
    nextCursor: page.nextCursor,
    hasMore: page.hasMore,
    source: 'firestore',
    error: null,
    empty: page.tracks.length === 0,
  };
}

function appendUniqueTracks(existing, incoming) {
  const seen = new Set(
    existing.map((track) => track.userPlaylistItemId || track.playlistItemId || track.id),
  );
  const next = [...existing];
  incoming.forEach((track) => {
    const key = track.userPlaylistItemId || track.playlistItemId || track.id;
    if (!key || seen.has(key)) return;
    seen.add(key);
    next.push(track);
  });
  return next;
}

/**
 * "My Music": the current user's own imported playlists, inside an active party.
 *
 * Private per user — playlist metadata is listed from `users/{uid}/playlists`
 * for the signed-in uid only. Already-imported tracks are read from the
 * namespaced Phase 4 item collection first, so browsing does not require a
 * YouTube OAuth token.
 *
 * Adding a track calls `queueStore.addToQueue`, the same path the search bar
 * uses, so `guestRequestsEnabled`, the guest cooldown, duplicate detection and
 * Firestore rules all still apply. Hosts are unaffected.
 */
export default function MyMusicPanel() {
  const navigate = useNavigate();
  const uid = useUserStore((state) => state.user?.uid ?? null);
  const isGoogleAccount = useUserStore((state) => state.user?.isGoogleAccount === true);
  const addToQueue = useQueueStore((state) => state.addToQueue);
  const eventId = useEventStore((state) => state.eventData?.id ?? null);

  const [playlists, setPlaylists] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openPlaylistId, setOpenPlaylistId] = React.useState(null);
  const [tracksByPlaylist, setTracksByPlaylist] = React.useState({});
  const [loadingTracks, setLoadingTracks] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [showPicker, setShowPicker] = React.useState(false);
  const [pickerHasList, setPickerHasList] = React.useState(false);
  const [menuPlaylistId, setMenuPlaylistId] = React.useState(null);
  const [removeTarget, setRemoveTarget] = React.useState(null);
  const [removingPlaylist, setRemovingPlaylist] = React.useState(false);
  const loadMoreInFlight = React.useRef(new Set());
  const upgradeInFlight = React.useRef(new Set());

  const reload = React.useCallback(async () => {
    if (!uid || !isGoogleAccount) {
      setPlaylists([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const imported = await fetchImportedPlaylists(uid);
    setPlaylists(imported);
    setLoading(false);
    Promise.resolve(refreshDueProviderMirrors(uid, { playlists: imported })).catch((error) => {
      console.error('Provider refresh deferred:', describeError(error));
    });
  }, [uid, isGoogleAccount]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const openPicker = () => {
    setPickerHasList(false);
    setShowPicker(true);
  };

  const closePicker = () => {
    setShowPicker(false);
    setPickerHasList(false);
  };

  const confirmRemovePlaylist = async () => {
    if (!removeTarget || removingPlaylist || !uid) return;
    const target = removeTarget;
    const targetId = target.playlistId;
    setRemovingPlaylist(true);
    try {
      await removePlaylistFromMyMusic(uid, target);
      setPlaylists((current) => current.filter((playlist) => playlist.playlistId !== targetId));
      setTracksByPlaylist((current) => {
        const next = { ...current };
        delete next[targetId];
        if (target.userPlaylistId) delete next[target.userPlaylistId];
        return next;
      });
      if (openPlaylistId === targetId || openPlaylistId === target.userPlaylistId) {
        setOpenPlaylistId(null);
      }
      setMenuPlaylistId(null);
      setRemoveTarget(null);
      toast.success('Removed from My Music');
    } catch (error) {
      console.error('Failed to remove playlist:', describeError(error));
      toast.error('Could not remove that playlist');
    } finally {
      setRemovingPlaylist(false);
    }
  };

  const handleGoogleSuccess = async () => {
    await reload();
  };

  const handleSignedInInstead = (result) => {
    const eventData = useEventStore.getState().eventData;
    const nextUid = result.user?.uid;
    const decision = resolveInPartyGoogleAuth({
      outcome: LINK_OUTCOME.signedInInstead,
      nextUid,
      eventData,
    });

    if (decision.action === IN_PARTY_GOOGLE_ACTION.stay) {
      const existing = findParticipant(eventData?.participants, nextUid);
      if (eventData?.hostId) {
        useRoleStore.getState().bindEventIdentity(eventData.hostId, existing?.role);
      }
      toast.success(`Signed in as ${result.user?.displayName || 'your Google account'}`);
      reload();
      return;
    }

    const nextEventId = eventData?.id;
    toast(
      'That Google account is a different Rhythmé identity. Join this party again to continue.',
    );
    navigate(nextEventId ? `/join/${nextEventId}` : '/', { replace: true });
    cleanupStores();
  };

  const loadFirestorePage = async (playlist, cursor = null) => (
    readUserPlaylistItemsPage({
      uid,
      playlist,
      cursor,
    })
  );

  const applyFirestorePage = (playlistId, page) => {
    setTracksByPlaylist((current) => ({
      ...current,
      [playlistId]: firestoreEntry(page),
    }));
  };

  const upgradeLegacyPlaylist = async (playlist) => {
    const playlistId = playlist.playlistId;
    if (upgradeInFlight.current.has(playlistId)) return;
    upgradeInFlight.current.add(playlistId);
    setTracksByPlaylist((current) => ({
      ...current,
      [playlistId]: {
        ...emptyPlaylistState(),
        source: 'upgrading',
      },
    }));

    try {
      const result = await syncImportedPlaylist(uid, playlist);
      const outcome = result?.outcome || result?.status;

      if (outcome === SYNC_OUTCOMES.BUSY) {
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...emptyPlaylistState(),
            source: 'legacy',
            error: 'upgrade-busy',
          },
        }));
        return;
      }

      if (outcome === SYNC_OUTCOMES.UNAUTHORIZED) {
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...emptyPlaylistState(),
            source: 'legacy',
            empty: true,
            error: 'unauthorized',
          },
        }));
        return;
      }

      if (outcome === SYNC_OUTCOMES.UNAVAILABLE) {
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...emptyPlaylistState(),
            source: 'firestore',
            error: 'unavailable',
          },
        }));
        return;
      }

      if (outcome === SYNC_OUTCOMES.FAILED) {
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...emptyPlaylistState(),
            source: 'legacy',
            error: 'upgrade-failed',
          },
        }));
        return;
      }

      const accepted = outcome === SYNC_OUTCOMES.COMPLETE
        || outcome === SYNC_OUTCOMES.PARTIAL
        || outcome === SYNC_OUTCOMES.FAST_PATH;
      if (!accepted) {
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...emptyPlaylistState(),
            source: 'legacy',
            error: 'upgrade-failed',
          },
        }));
        return;
      }

      const page = await loadFirestorePage(playlist);
      const expectedCount = Number.isSafeInteger(playlist?.itemCount)
        ? playlist.itemCount
        : 0;
      if (page.fetchedCount === 0 && expectedCount > 0) {
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...emptyPlaylistState(),
            source: 'legacy',
            error: 'upgrade-failed',
          },
        }));
        return;
      }
      applyFirestorePage(playlistId, page);
    } catch (error) {
      if (error instanceof UserLibraryReadError || error?.code === 'library-read') {
        console.error('Failed to load playlist library:', describeError(error));
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...emptyPlaylistState(),
            source: 'firestore',
            error: 'library-read',
          },
        }));
        return;
      }
      console.error('Failed to persist imported playlist library:', describeError(error));
      setTracksByPlaylist((current) => ({
        ...current,
        [playlistId]: {
          ...emptyPlaylistState(),
          source: 'legacy',
          error: 'upgrade-failed',
        },
      }));
    } finally {
      upgradeInFlight.current.delete(playlistId);
    }
  };

  const loadFirstPage = async (playlist) => {
    const playlistId = playlist.playlistId;
    setLoadingTracks(true);
    setTracksByPlaylist((current) => ({
      ...current,
      [playlistId]: {
        ...(current[playlistId] || emptyPlaylistState()),
        error: null,
      },
    }));

    try {
      const page = await loadFirestorePage(playlist);
      const expectedCount = Number.isSafeInteger(playlist?.itemCount)
        ? playlist.itemCount
        : 0;
      const emptyPhase4Shell = page.phase4PlaylistExists
        && page.fetchedCount === 0
        && expectedCount > 0;
      const incompleteCoverage = coverageLooksIncomplete(page, playlist);
      if (
        !incompleteCoverage
        && (page.fetchedCount > 0 || (page.phase4PlaylistExists && !emptyPhase4Shell))
      ) {
        applyFirestorePage(playlistId, page);
        return;
      }

      if (!hasYouTubeAuthorization()) {
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...emptyPlaylistState(),
            source: 'legacy',
            empty: true,
          },
        }));
        return;
      }

      await upgradeLegacyPlaylist(playlist);
    } catch (error) {
      if (error instanceof UserLibraryReadError || error?.code === 'library-read') {
        console.error('Failed to load playlist library:', describeError(error));
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...(current[playlistId] || emptyPlaylistState()),
            source: 'firestore',
            error: 'library-read',
          },
        }));
        return;
      }

      console.error('Failed to load playlist tracks:', describeError(error));
      toast.error(describePlaylistLoadFailure(error).message);
    } finally {
      setLoadingTracks(false);
    }
  };

  const openPlaylist = async (playlist) => {
    const playlistId = playlist.playlistId;
    if (openPlaylistId === playlistId) {
      setOpenPlaylistId(null);
      return;
    }

    setOpenPlaylistId(playlistId);
    const existing = tracksByPlaylist[playlistId];
    if (upgradeInFlight.current.has(playlistId)) {
      return;
    }
    if (existing?.source === 'upgrading' && !existing.error) {
      return;
    }
    if (existing && existing.source === 'firestore' && !existing.error) {
      return;
    }
    await loadFirstPage(playlist);
  };

  const loadMore = async (playlist) => {
    const playlistId = playlist.playlistId;
    const existing = tracksByPlaylist[playlistId];
    if (!existing?.hasMore || !existing.nextCursor || loadingMore) return;
    if (loadMoreInFlight.current.has(playlistId)) return;

    loadMoreInFlight.current.add(playlistId);
    setLoadingMore(true);
    try {
      const page = await loadFirestorePage(playlist, existing.nextCursor);
      setTracksByPlaylist((current) => {
        const prior = current[playlistId] || emptyPlaylistState();
        return {
          ...current,
          [playlistId]: {
            ...prior,
            tracks: appendUniqueTracks(prior.tracks, page.tracks),
            nextCursor: page.nextCursor,
            hasMore: page.hasMore,
            source: 'firestore',
            error: null,
            empty: false,
          },
        };
      });
    } catch (error) {
      if (error instanceof UserLibraryReadError || error?.code === 'library-read') {
        console.error('Failed to load playlist library:', describeError(error));
        setTracksByPlaylist((current) => ({
          ...current,
          [playlistId]: {
            ...(current[playlistId] || emptyPlaylistState()),
            error: 'library-read',
          },
        }));
        return;
      }
      console.error('Failed to load playlist tracks:', describeError(error));
      toast.error(describePlaylistLoadFailure(error).message);
    } finally {
      loadMoreInFlight.current.delete(playlistId);
      setLoadingMore(false);
    }
  };

  if (!uid) {
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-sm text-gray-400">Connecting your session…</p>
      </div>
    );
  }

  if (!isGoogleAccount) {
    return (
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <p className="text-sm text-gray-300">
          Continue with Google here to bring your YouTube playlists into this party. You can keep
          requesting songs with search in the meantime.
        </p>
        <GoogleAuthButton
          onSuccess={handleGoogleSuccess}
          onSignedInInstead={handleSignedInInstead}
          redirectContext={
            eventId
              ? { eventCode: eventId, returnPath: `/party/${eventId}` }
              : null
          }
        />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {loading && <p className="text-sm text-gray-400">Loading your music…</p>}

      <FavoritesSection
        uid={uid}
        canAdd={Boolean(eventId)}
        onAddToQueue={(track) => addToQueue(track)}
      />

      {!loading && playlists.length === 0 && (
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-medium text-white">Playlists</h3>
            <p className="text-xs text-gray-400">Connected playlists</p>
          </div>
          <p className="text-sm text-gray-300">
            You haven&apos;t imported any playlists yet.
          </p>
          <button
            type="button"
            onClick={openPicker}
            className="w-full rounded-lg bg-red-600 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
          >
            Connect YouTube playlists
          </button>
        </div>
      )}

      {!loading && playlists.length > 0 && (
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-medium text-white">Playlists</h3>
            <p className="text-xs text-gray-400">Connected playlists</p>
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={openPicker}
              className="w-full rounded-lg bg-white/10 py-2 text-sm text-white hover:bg-white/20 transition-colors"
            >
              Add / manage playlists
            </button>
          </div>
        </div>
      )}

      {playlists.map((playlist) => {
        const entry = tracksByPlaylist[playlist.playlistId] || emptyPlaylistState();
        const tracks = entry.tracks;
        const isOpen = openPlaylistId === playlist.playlistId;
        const needsReconnect = isOpen
          && tracks.length === 0
          && (
            entry.error === 'unauthorized'
            || (
              entry.source === 'legacy'
              && !hasYouTubeAuthorization()
              && !entry.error
            )
          );

        return (
          <div key={playlist.playlistId} className="rounded-lg bg-white/5">
            <div className="flex items-center">
            <button
              type="button"
              onClick={() => {
                setMenuPlaylistId(null);
                openPlaylist(playlist);
              }}
              className="flex min-w-0 flex-1 items-center gap-3 p-2 text-left hover:bg-white/10 rounded-lg"
              aria-expanded={isOpen}
            >
              {playlist.thumbnail ? (
                <img src={playlist.thumbnail} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
              ) : (
                <div className="h-10 w-10 shrink-0 rounded bg-white/10" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-white">{playlist.title}</span>
                <span className="block text-xs text-gray-400">
                  {isOpen && tracks.length > 0
                    ? `${tracks.length} playable`
                    : `${playlist.itemCount} tracks`}
                </span>
              </span>
            </button>
            <button
              type="button"
              aria-label="More actions"
              aria-haspopup="menu"
              onClick={() => setMenuPlaylistId((current) => (
                current === playlist.playlistId ? null : playlist.playlistId
              ))}
              className="mr-1 shrink-0 rounded-lg p-2 text-gray-300 hover:bg-white/10 hover:text-white"
            >
              <EllipsisHorizontalIcon className="h-5 w-5" />
            </button>
            </div>
            {menuPlaylistId === playlist.playlistId && (
              <div className="px-2 pb-2" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  disabled={entry.source === 'upgrading' || removingPlaylist}
                  onClick={() => {
                    setMenuPlaylistId(null);
                    setRemoveTarget(playlist);
                  }}
                  className="w-full rounded-lg px-3 py-2 text-left text-sm text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Remove from My Music
                </button>
              </div>
            )}

            {isOpen && (
              <div className="space-y-1 px-2 pb-2">
                {needsReconnect && (
                  <div className="space-y-2 px-1 py-1">
                    <p className="text-xs text-gray-400">Reconnect YouTube to load tracks.</p>
                    <button
                      type="button"
                      onClick={openPicker}
                      className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors"
                    >
                      Reconnect YouTube
                    </button>
                  </div>
                )}

                {entry.error === 'library-read' && (
                  <div className="space-y-2 px-1 py-1" data-testid="library-read-error">
                    <p className="text-xs text-gray-400">
                      Could not load your library. Check your connection and try again.
                    </p>
                    <button
                      type="button"
                      onClick={() => loadFirstPage(playlist)}
                      className="rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20 transition-colors"
                    >
                      Try again
                    </button>
                  </div>
                )}

                {entry.error === 'upgrade-failed' && (
                  <div className="space-y-2 px-1 py-1" data-testid="library-upgrade-error">
                    <p className="text-xs text-gray-400">
                      Could not save this playlist to Rhythmé. Try again.
                    </p>
                    <button
                      type="button"
                      onClick={() => loadFirstPage(playlist)}
                      className="rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20 transition-colors"
                    >
                      Try again
                    </button>
                  </div>
                )}

                {entry.error === 'unavailable' && (
                  <p className="px-1 text-xs text-gray-400" data-testid="playlist-unavailable">
                    This playlist is no longer available on YouTube.
                  </p>
                )}

                {entry.error === 'upgrade-busy' && (
                  <div className="space-y-2 px-1 py-1" data-testid="playlist-upgrade-busy">
                    <p className="text-xs text-gray-400">
                      This playlist is already being saved.
                    </p>
                    <button
                      type="button"
                      onClick={() => loadFirstPage(playlist)}
                      className="rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20 transition-colors"
                    >
                      Try again
                    </button>
                  </div>
                )}

                {(entry.source === 'upgrading' || (loadingTracks && tracks.length === 0))
                  && !needsReconnect
                  && !entry.error && (
                  <p className="px-1 text-xs text-gray-400" data-testid="playlist-upgrading">
                    {entry.source === 'upgrading'
                      ? 'Saving this playlist to Rhythmé…'
                      : 'Loading tracks…'}
                  </p>
                )}

                {entry.empty && entry.source === 'firestore' && !loadingTracks && !entry.error && (
                  <p className="px-1 text-xs text-gray-400" data-testid="playlist-empty">
                    No tracks in this playlist yet.
                  </p>
                )}

                {tracks.map((track) => (
                  <div
                    key={track.userPlaylistItemId || track.playlistItemId || `${track.id}-${track.position}`}
                    className="flex items-center gap-2 rounded-lg bg-black/20 p-2"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-white">{track.title}</span>
                      {track.artistSubtitle ? (
                        <span className="block truncate text-xs text-gray-400">
                          {track.artistSubtitle}
                        </span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      onClick={() => addToQueue(track)}
                      aria-label={`Add ${track.title} to queue`}
                      className="shrink-0 rounded-lg bg-blue-600 p-1.5 text-white hover:bg-blue-700 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                {entry.hasMore && (
                  <button
                    type="button"
                    data-testid="playlist-load-more"
                    disabled={loadingMore}
                    onClick={() => loadMore(playlist)}
                    className="mt-1 w-full rounded-lg bg-white/10 py-1.5 text-xs text-white hover:bg-white/20 transition-colors disabled:opacity-60"
                  >
                    {loadingMore ? 'Loading…' : 'Load More'}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      <Modal
        open={Boolean(removeTarget)}
        title="Remove from My Music?"
        onClose={() => {
          if (!removingPlaylist) setRemoveTarget(null);
        }}
        size="sm"
        footer={(
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setRemoveTarget(null)}
              disabled={removingPlaylist}
              className="flex-1 rounded-lg bg-white/10 py-2 text-sm text-white hover:bg-white/20 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmRemovePlaylist}
              disabled={removingPlaylist}
              className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
            >
              {removingPlaylist ? 'Removing…' : 'Remove'}
            </button>
          </div>
        )}
      >
        <p className="text-sm text-gray-300">
          Remove “{removeTarget?.title || 'this playlist'}” and its imported tracks from Rhythmé?
          This will not delete or change the playlist on YouTube.
        </p>
      </Modal>

      <Modal
        open={showPicker}
        title="Your YouTube playlists"
        onClose={closePicker}
        layout={pickerHasList ? 'fill' : 'stack'}
        resizable={pickerHasList}
      >
        <PlaylistPicker
          connectedPlaylists={playlists}
          onListVisibilityChange={setPickerHasList}
          onImported={async (syncedPlaylists) => {
            closePicker();
            const synced = Array.isArray(syncedPlaylists) ? syncedPlaylists : [];
            const syncedIds = new Set(
              synced.map((playlist) => playlist?.playlistId).filter(Boolean),
            );
            if (syncedIds.size > 0) {
              setTracksByPlaylist((current) => {
                const next = { ...current };
                syncedIds.forEach((playlistId) => {
                  delete next[playlistId];
                });
                return next;
              });
            }
            await reload();
            if (openPlaylistId && syncedIds.has(openPlaylistId)) {
              const playlist = synced.find((entry) => entry?.playlistId === openPlaylistId);
              if (playlist) await loadFirstPage(playlist);
            }
          }}
          onDisconnected={async () => {
            closePicker();
            await reload();
          }}
          onSkip={closePicker}
          skipLabel="Cancel"
        />
      </Modal>
    </div>
  );
}
